// web/src/components/rtls/EditAssetDialog.tsx
//
// Modal dialog for editing an asset row (personnel or equipment). Mirrors
// AddAccessPointDialog's structure but writes to two different tables:
//   - asset.type === 'personnel' → UPDATE public.personnel
//   - asset.type === 'equipment' → UPDATE public.equipment
//
// Status enums differ per table (personnel: tagged_in/tagged_out/missing;
// equipment: active/down/maintenance/idle), so the dropdown options swap
// based on type. Other RTLS-visible fields (name, externalId, subtype,
// level, x/y/z) are common to both.
//
// Optimistic flow: dispatch updateSingleAsset → close dialog → run UPDATE.
// On error, dispatch the previous row back and toast the failure.

import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { useAppDispatch } from '../../app/useAppDispatch'
import { useAppSelector } from '../../app/useAppSelector'
import { rtlsActions } from '@shared/slices/rtlsSlice'
import { uiActions } from '@shared/slices/uiSlice'
import { supabase } from '../../app/supabaseClient'
import type { RTLSAsset } from '@shared/types/rtlsTypes'
import { showToast } from '../Toast'

// DB-native status enums per table. Different from the RTLS-normalized
// AssetStatus surfaced by the assets view — we expose the raw DB values
// here so operator edits round-trip exactly.
const PERSONNEL_STATUS_OPTIONS = ['tagged_in', 'tagged_out', 'missing'] as const
const EQUIPMENT_STATUS_OPTIONS = ['active', 'down', 'maintenance', 'idle'] as const

// Known subtype enums per type. Kept as `<input list>` so operators can
// type a value that isn't yet enumerated (subtype is TEXT in the DB).
const PERSONNEL_SUBTYPES = ['worker', 'supervisor', 'emergency_team']
const EQUIPMENT_SUBTYPES = ['truck', 'loader', 'drill_rig', 'rescue_vehicle', 'other']

interface EditAssetDialogProps {
  open: boolean
  onClose: () => void
  /** ID of the asset to edit. Null = dialog closed (no-op render). */
  editingId: string | null
}

export const EditAssetDialog: React.FC<EditAssetDialogProps> = ({
  open,
  onClose,
  editingId,
}) => {
  const dispatch = useAppDispatch()
  const tappedCoord  = useAppSelector(s => s.ui.tappedCoord)
  const assetsRecord = useAppSelector(s => s.rtls.assets)

  const asset = editingId ? assetsRecord[editingId] : null
  const isPersonnel = asset?.type === 'personnel'

  // Form state — all string-typed so number inputs can hold transient
  // empty/intermediate values (e.g., "-" while typing a negative number).
  const [name, setName]             = useState('')
  const [externalId, setExternalId] = useState('')
  const [subtype, setSubtype]       = useState('')
  const [status, setStatus]         = useState<string>('')
  const [level, setLevel]           = useState('')
  const [x, setX]                   = useState('0')
  const [y, setY]                   = useState('0')
  const [z, setZ]                   = useState('0')
  const [submitting, setSubmitting] = useState(false)

  // Prefill when the dialog opens with a new editingId. The Redux `asset`
  // comes from the `assets` VIEW which normalizes per-type status enums
  // into the RTLS palette (active/idle/alert/offline). The underlying
  // tables use their own enums (personnel_status / equipment_status), so
  // we fetch the raw row directly — otherwise a normalized 'alert' lands
  // in the dropdown state and the UPDATE fails with PG 22P02.
  useEffect(() => {
    if (!open || !asset || !editingId) return
    // Optimistic prefill from Redux while the underlying-table fetch lands —
    // gets the dialog populated immediately. Status is intentionally
    // skipped here (Redux value is view-normalized, not DB-valid).
    setName(asset.name ?? '')
    setExternalId(asset.externalId ?? '')
    setSubtype(asset.subType ?? '')
    setStatus('')
    setLevel(asset.level != null ? String(asset.level) : '')
    setX(asset.position.x.toFixed(2))
    setY(asset.position.y.toFixed(2))
    setZ(asset.position.z.toFixed(2))

    let cancelled = false
    const table = asset.type === 'personnel' ? 'personnel' : 'equipment'
    const idColumn = asset.type === 'personnel' ? 'employee_id' : 'asset_id'
    void supabase
      .from(table)
      .select(`name, ${idColumn}, subtype, status, level, x, y, z`)
      .eq('id', editingId)
      .single()
      .then(({ data, error }) => {
        if (cancelled) return
        if (error || !data) {
          // Fall back to the (still-Redux-sourced) values already in state.
          // Status stays empty; the user must explicitly pick one to save.
          return
        }
        const row = data as Record<string, unknown>
        if (typeof row.name === 'string') setName(row.name)
        if (typeof row[idColumn] === 'string') setExternalId(row[idColumn] as string)
        if (typeof row.subtype === 'string') setSubtype(row.subtype)
        if (typeof row.status === 'string') setStatus(row.status)
        if (typeof row.level === 'number') setLevel(String(row.level))
        if (typeof row.x === 'number') setX(row.x.toFixed(2))
        if (typeof row.y === 'number') setY(row.y.toFixed(2))
        if (typeof row.z === 'number') setZ(row.z.toFixed(2))
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingId])

  // Mid-session map tap re-positions x/y/z.
  useEffect(() => {
    if (!open || !tappedCoord) return
    setX(tappedCoord.x.toFixed(2))
    setY(tappedCoord.y.toFixed(2))
    setZ(tappedCoord.z.toFixed(2))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tappedCoord])

  // Reset on close.
  useEffect(() => {
    if (!open) {
      setSubmitting(false)
      dispatch(uiActions.setTappedCoord(null))
    }
  }, [open, dispatch])

  if (!open || !asset) return null

  const statusOptions = isPersonnel ? PERSONNEL_STATUS_OPTIONS : EQUIPMENT_STATUS_OPTIONS
  const subtypeOptions = isPersonnel ? PERSONNEL_SUBTYPES : EQUIPMENT_SUBTYPES
  const idFieldLabel   = isPersonnel ? 'Employee ID' : 'Asset ID'
  const tableName      = isPersonnel ? 'personnel' : 'equipment'

  const trimmedName       = name.trim()
  const trimmedExternalId = externalId.trim()
  const trimmedSubtype    = subtype.trim()
  const xNum = parseFloat(x)
  const yNum = parseFloat(y)
  const zNum = parseFloat(z)
  const levelNum = level.trim() ? parseInt(level, 10) : null
  const canSubmit =
    !!trimmedName &&
    !!status &&
    [xNum, yNum, zNum].every(Number.isFinite) &&
    !submitting

  const handleSubmit = async () => {
    if (!canSubmit || !asset) return
    setSubmitting(true)

    const prev = asset
    const updated: RTLSAsset = {
      ...prev,
      name: trimmedName,
      externalId: trimmedExternalId || undefined,
      subType: (trimmedSubtype || undefined) as RTLSAsset['subType'],
      // status is the DB-native enum; cast to RTLSAsset['status'] for the
      // optimistic Redux update. The assets view will re-normalize on the
      // next realtime push.
      status: status as RTLSAsset['status'],
      position: { x: xNum, y: yNum, z: zNum },
      level: levelNum ?? undefined,
      lastUpdated: Date.now(),
    }
    dispatch(rtlsActions.updateSingleAsset(updated))
    onClose()

    // Build the update payload using the DB column names. personnel uses
    // employee_id; equipment uses asset_id.
    const idColumn = isPersonnel ? 'employee_id' : 'asset_id'
    const payload: Record<string, unknown> = {
      name: trimmedName,
      subtype: trimmedSubtype || null,
      status,
      x: xNum,
      y: yNum,
      z: zNum,
      level: levelNum,
      last_updated: new Date().toISOString(),
    }
    // Only include the external ID column when the operator actually
    // changed it — these are UNIQUE constraints and an unnecessary write
    // of the existing value would still trigger the constraint check.
    if (trimmedExternalId && trimmedExternalId !== prev.externalId) {
      payload[idColumn] = trimmedExternalId
    }

    const { error } = await supabase
      .from(tableName)
      .update(payload)
      .eq('id', prev.id)

    if (error) {
      dispatch(rtlsActions.updateSingleAsset(prev))
      showToast(`Failed to save: ${error.message}`, 'error')
    } else {
      showToast(`${isPersonnel ? 'Personnel' : 'Equipment'} updated.`, 'success')
    }
  }

  const fieldCls =
    'w-full rounded-sm border border-surfaceBorder bg-surfaceCard px-sm py-xs text-sm text-textPrimary focus:border-primary focus:outline-none'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Edit ${isPersonnel ? 'personnel' : 'equipment'}`}
      onClick={onClose}
    >
      <div
        className="w-96 rounded-md border border-surfaceBorder bg-surface p-lg shadow-lg"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="mb-md text-lg font-semibold text-textPrimary">
          Edit {isPersonnel ? 'personnel' : 'equipment'}
        </h2>

        <div className="flex flex-col gap-sm">
          <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
            Name
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
              className={fieldCls}
            />
          </label>

          <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
            {idFieldLabel}
            <input
              type="text"
              value={externalId}
              onChange={e => setExternalId(e.target.value)}
              className={fieldCls}
              placeholder={isPersonnel ? 'EMP_235' : 'HT102'}
            />
          </label>

          <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
            Subtype
            <input
              type="text"
              list="asset-subtype-options"
              value={subtype}
              onChange={e => setSubtype(e.target.value)}
              className={fieldCls}
              placeholder={subtypeOptions[0]}
            />
            <datalist id="asset-subtype-options">
              {subtypeOptions.map(s => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </label>

          <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
            Status
            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              className={fieldCls}
            >
              {statusOptions.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
            Level (optional)
            <input
              type="text"
              inputMode="numeric"
              value={level}
              onChange={e => setLevel(e.target.value)}
              className={fieldCls}
              placeholder="e.g. 2"
            />
          </label>

          <div className="grid grid-cols-3 gap-sm">
            <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
              X
              <input type="text" inputMode="decimal" value={x} onChange={e => setX(e.target.value)} className={fieldCls} />
            </label>
            <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
              Y
              <input type="text" inputMode="decimal" value={y} onChange={e => setY(e.target.value)} className={fieldCls} />
            </label>
            <label className="flex flex-col gap-xs text-xs font-semibold uppercase text-textMuted">
              Z
              <input type="text" inputMode="decimal" value={z} onChange={e => setZ(e.target.value)} className={fieldCls} />
            </label>
          </div>

          {tappedCoord && (
            <p className="text-xs text-textMuted">
              Tip: click a point on the 3D map to pre-fill X/Y/Z.
            </p>
          )}
        </div>

        <div className="mt-lg flex justify-end gap-sm">
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm px-md py-xs text-sm text-textMuted hover:text-textPrimary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'rounded-sm px-md py-xs text-sm font-semibold transition-colors',
              canSubmit
                ? 'bg-primary text-black hover:bg-primaryDark'
                : 'cursor-not-allowed bg-surfaceCard text-textDisabled',
            )}
          >
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
